import { inject, Injectable } from '@angular/core';
import { SharedStorageService } from './shared-storage.service';
import { IdleTimeoutService } from './idle-timeout.service';
import { BehaviorSubject, catchError, filter, Observable, of, retry, shareReplay, switchMap, take, throwError } from 'rxjs';
import { HttpClient, HttpEvent, HttpHandlerFn, HttpRequest, HttpHeaders } from '@angular/common/http';
import { ApiResponse } from '../interfaces/api-response.model';
import { API_ENDPOINTS } from '../constants/api.constants';
import { AuthResponse, DuplicateSessionsRequest } from '../interfaces/login-register.model';


// 新增 JWT 解碼函數
function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly isBrowser = typeof window !== 'undefined';
  private readonly baseKey = 'accessToken';
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private refreshObservable: Observable<ApiResponse<AuthResponse> | null> | null = null;
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  private idleTimeoutService: IdleTimeoutService = inject(IdleTimeoutService);
  http: HttpClient = inject(HttpClient);

  get token(): string | null {
    return this.isBrowser ? localStorage.getItem(this.key) : null;
  }

  get login(): boolean {
    return !!this.token;
  }

  set login(v: boolean) {
    console.log('login setter has been called，value:', v);
    v ? this.performLogin('[DEV_TOKEN]') : this.performLogout().subscribe({
      next: (res) => {
        this.clearToken();
        console.log('登出 API 成功:', res);
      },
      error: (error) => {
        this.clearToken();
        console.error('登出 API 失敗:', error);
      }
    });
  }

  isLogin(): boolean {
    const token = this.token;
    return !!token && !this.isTokenExpired(token);
  }

  /**
   * 登入
   * @param token
   */
  performLogin(token: string): void {
  console.log('performLogin has been called，token:', token);
    this.setToken(token);
    this.idleTimeoutService.startWatching();
  }

  /**
   * 登出
   */
  performLogout(): Observable<any> {
    const currentToken = this.token;
    this.idleTimeoutService.stopWatching();
    // this.clearToken();
    this.sharedStorageService.clear();
    this.sharedStorageService.remove('createMemberRequest');
    this.sharedStorageService.remove('myReservation');
    this.sharedStorageService.remove('savedQuestionnaireData');
    console.log("currentToken:", currentToken);
    
    return this.sessionsLogout(currentToken);
  }

  setToken(token: string): void {
  console.log('setToken has been called，token:', token);
  if (this.isBrowser && token) {
    localStorage.setItem(this.key, token);
    // 設置 token 後自動啟動閒置監控
    this.idleTimeoutService.startWatching();
    console.log('setToken 後自動啟動閒置監控');
  }
}

  clearToken(): void {
    if (this.isBrowser) localStorage.removeItem(this.key);
    this.sharedStorageService.clear();
  }

  // 判斷 token 是否過期（包含滑動時間）
  isTokenExpired(token?: string): boolean {
    const currentToken = token || this.token;
    if (!currentToken) return true;

    const decoded = parseJwt(currentToken);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    const currentTimeMs = Date.now();

    // 檢查最長有效時間（秒）
    const isMaxTimeExpired = decoded.exp < currentTime;
    
    // 檢查滑動時間（毫秒）
    // const isIdleExpired = decoded.idle && decoded.idle < currentTimeMs;

    // console.log('Current time (ms):', currentTimeMs);
    // console.log('Token idle time:', decoded.idle);
    // console.log('Token max exp:', decoded.exp);
    // console.log('Idle expired?', isIdleExpired);
    // console.log('Max time expired?', isMaxTimeExpired);

    // 任一過期就算過期
    return isMaxTimeExpired;
  }

  // 檢查滑動時間是否過期
  isIdleTimeExpired(token?: string): boolean {
    const currentToken = token || this.token;
    if (!currentToken) return true;

    const decoded = parseJwt(currentToken);
    if (!decoded || !decoded.idle) return true;

    const currentTimeMs = Date.now();
    return decoded.idle < currentTimeMs;
  }

  // 檢查token是否在5分鐘後過期(預設5分鐘)
  isTokenExpiringSoon(token?: string, bufferMinutes: number = 5): boolean {
    const currentToken = token || this.token;
    if (!currentToken) return true;

    const decoded = parseJwt(currentToken);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = bufferMinutes * 60; // 將分鐘轉換為秒

    // 計算token 還剩多少時間過期
    return decoded.exp - currentTime < bufferTime;
  }

  // 取得有效的token，如果token無效或過期，則刷新token
  getValidToken(): Observable<ApiResponse<AuthResponse>> {
    const token = this.token;
    // token無效或滑動時間過期，刷新token
    if (!token || this.isIdleTimeExpired(token)) {
      console.log('token無效或過期，刷新token');
      return this.refreshSessions().pipe(
        retry({ count: 1, delay: 1000 }), // 失敗後重試一次，延遲1秒
        switchMap((res) => {
          if (res?.data?.accessToken) {
            console.log('刷新token成功，設定新token');
            this.setToken(res.data.accessToken);
          }
          return of(res);
        }),
        catchError((error) => {
          console.error('刷新 token 失敗:', error);
          return throwError(() => error);
        })  
      );
    } 
    // token有效，回傳包含當前token的AuthResponse
    console.log("token有效，回傳包含當前token的AuthResponse");
    return of({ 
      success: true,
      statusCode: 'SUCCESS',
      data: { accessToken: token },
      timestamp: new Date().toISOString(),
     });
  }

  /**
   * 延長 token 有效時間
   */
  refreshSessions(): Observable<ApiResponse<AuthResponse>> {
    return this.http.post<ApiResponse<any>>(API_ENDPOINTS.REFRESH_SESSIONS, {});
  }

  /**
   * 使用者登出
   * @param token 可選的 accessToken
   */
  sessionsLogout(token?: string | null): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(API_ENDPOINTS.SESSIONS_LOGOUT, {});
  }

  /**
   * 取得異地登入token
   */
  getDuplicateSessions(idNo: string): Observable<ApiResponse<AuthResponse>> {
    const payload: DuplicateSessionsRequest = {
      idNo: idNo,
    };
    return this.http.post<ApiResponse<any>>(API_ENDPOINTS.GET_DUPLICATE_SESSIONS, payload);
  }

  // 解決同時間呼叫refreshToken的問題
  handleTokenRefresh(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(newToken => newToken !== null),
        take(1),
        switchMap((newToken) => {
          const newAuthReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
          });
          return next(newAuthReq);
        })
      );
    } else {
      this.refreshTokenInProgress = true;
      this.refreshObservable = this.refreshSessions().pipe(
        retry({count: 1, delay: 1000}),
        shareReplay(1),
        switchMap((response) => {
          if (response.data?.accessToken) { 
            this.setToken(response.data?.accessToken);
            this.refreshTokenSubject.next(response.data?.accessToken);
            this.refreshTokenInProgress = false;
            this.refreshObservable = null;
            return of(response);
          }
          return of(null);
        }),
        catchError((refreshError) => {
          this.refreshTokenSubject.next(null);
          this.refreshTokenInProgress = false;
          this.refreshObservable = null;
          this.performLogout().subscribe({
            next: (res) => {
              this.clearToken();
              console.log('登出 API 成功:', res);
            },
            error: (error) => {
              this.clearToken();
              console.error('登出 API 失敗:', error);
            }
          });
          return throwError(() => refreshError);
        })
      );
      return this.refreshObservable!.pipe(
        switchMap((response) => {
          if (response?.data?.accessToken) {
            const newAuthReq = req.clone({
              setHeaders: { Authorization: `Bearer ${response.data.accessToken}` },
            });
            return next(newAuthReq);
          } else {
            // 如果沒有有效的 token，直接發送原始請求或拋出錯誤
            this.performLogout().subscribe({
              next: (res) => {
                this.clearToken();
                console.log('登出 API 成功:', res);
              },
              error: (error) => {
                this.clearToken();
                console.error('登出 API 失敗:', error);
              }
            });
            return throwError(() => new Error('無法取得有效的 access token'));
          }
        })
      );
    }
  }
  // 根據環境取得唯一的 token key
  private get key(): string {
    if (!this.isBrowser) return this.baseKey;
    
    const hostname = window.location.hostname;
    const isDevEnv = hostname.includes('dev');
    const isUatEnv = hostname.includes('uat');
    const envPrefix = isDevEnv ? 'dev' : isUatEnv ? 'uat' : 'prod';
    
    return `${this.baseKey}_${envPrefix}`;
  }
}
